import secureStorage from '../../lib/secureStorage';
import React, { useState, useEffect, useRef } from 'react';
import authedFetch from '../../lib/authedFetch';
import {
  Building2, DollarSign, Settings, TrendingUp, Calculator, Sparkles, AlertCircle,
  Trash2, Loader2, Clock, BookOpen, Image, Upload, Users, Save, Copy, Link2, Check, MessageSquareText, Layers
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import OrgTimezoneField from './OrgTimezoneField';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { getCustomPresets, createCustomPreset, deleteCustomPreset, uploadLogo, deleteLogo, getLogo } from '../../lib/api';
import { MaturitySliderWithGuide } from '../MaturitySliderWithGuide';
import { toast } from 'sonner';
import { OrgDefaultsTab } from './OrgDefaultsTab';
import MirrorDisplayPanel from './MirrorDisplayPanel';
import IndustryModelPanel from './IndustryModelPanel';
import SavePresetDialog from './SavePresetDialog';
import logger from '../../lib/logger';
import useIndustryProfilesList from '../../hooks/useIndustryProfilesList';

export default function OrgSettingsPanel({ orgId, onSaved }) {
  const { user, updateProfile } = useAuth();
  const { industries: industryProfiles } = useIndustryProfilesList();
  const [isLoading, setIsLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [copiedId, setCopiedId] = useState(false);
  
  // Organization record (for org ID + external ID)
  const [orgRecord, setOrgRecord] = useState(null);
  const [externalId, setExternalId] = useState('');
  const [savingExternalId, setSavingExternalId] = useState(false);
  
  // Track whether we're editing another org (not the logged-in user's own)
  const [isExternalOrg, setIsExternalOrg] = useState(false);
  const [orgName, setOrgName] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const DEFAULT_PROFILE = {
    organization_name: '', industry_type: 'technology', business_type: 'enterprise',
    company_size: 'large', total_workforce: 2000, eligible_population: 1500,
    revenue_per_employee: 150000, annual_revenue: 300000000, operating_margin: 15,
    default_currency: 'USD', default_intervention_budget: 500000,
    preferred_elasticity_scenario: 'moderate', fiscal_year_start: 'january',
    region: 'north_america', current_maturity: 50,
    dampening_factors: { adoption_speed: 0.7, leadership_alignment: 0.8, operational_readiness: 0.75 },
    scale_dampening_config: { mode: 'automatic', custom_multiplier: 1.0, fixed_factor: 1.0 },
    time_lag_config: { implementation_lag_months: 2, ramp_up_months: 3, annual_decay_rate: 0.15, include_decay: false },
    primary_learning_modality: 'blended'
  };
  const [profile, setProfile] = useState(DEFAULT_PROFILE);

  const [customPresets, setCustomPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPreset, setNewPreset] = useState({ name: '', description: '', icon: '⚙️', color: 'slate' });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    const loadPresets = async () => {
      if (!user) return;
      setLoadingPresets(true);
      try {
        const response = await getCustomPresets();
        setCustomPresets(response.data || []);
      } catch (error) { logger.error('Failed to load custom presets:', error); }
      finally { setLoadingPresets(false); }
    };
    loadPresets();
  }, [user]);

  useEffect(() => {
    if (user && !isExternalOrg) {
      if (user.profile?.company_logo) setCompanyLogo(user.profile.company_logo);
      setProfile(prev => ({
        ...prev, ...user.profile,
        dampening_factors: { ...prev.dampening_factors, ...user.profile?.dampening_factors },
        scale_dampening_config: { ...prev.scale_dampening_config, ...user.profile?.scale_dampening_config },
        time_lag_config: { ...prev.time_lag_config, ...user.profile?.time_lag_config }
      }));
    }
  }, [user, isExternalOrg]);

  useEffect(() => {
    const fetchLogo = async () => {
      if (!user || isExternalOrg) return;
      try {
        const response = await getLogo();
        if (response.data?.logo) setCompanyLogo(response.data.logo);
      } catch (error) { /* silent */ }
    };
    fetchLogo();
  }, [user, isExternalOrg]);
  
  // Fetch organization record and determine if it's an external org
  useEffect(() => {
    if (!orgId) return;
    const fetchOrg = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations/${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrgRecord(data);
          setExternalId(data.external_id || '');
          setOrgName(data.name || '');
          
          // Check if this is a different org than the user's own
          const userOrgIds = user?.organizations?.map(o => typeof o === 'string' ? o : o.id) || [];
          const isExternal = !userOrgIds.includes(orgId);
          setIsExternalOrg(isExternal);
          
          // Load org-level settings if they exist
          if (data.settings) {
            setProfile(prev => ({
              ...prev,
              ...data.settings,
              organization_name: data.name || data.settings.organization_name || '',
              dampening_factors: { ...prev.dampening_factors, ...data.settings.dampening_factors },
              scale_dampening_config: { ...prev.scale_dampening_config, ...data.settings.scale_dampening_config },
              time_lag_config: { ...prev.time_lag_config, ...data.settings.time_lag_config }
            }));
          } else {
            // No org-level settings yet — at least set the name
            setProfile(prev => ({ ...prev, organization_name: data.name || '' }));
          }
          
          // Load org logo if stored in settings
          if (data.settings?.company_logo) {
            setCompanyLogo(data.settings.company_logo);
          } else if (isExternal) {
            setCompanyLogo(null);
          }
        }
      } catch (e) { logger.error('Error fetching org record:', e); }
    };
    fetchOrg();
  }, [orgId, API_URL, user]);
  
  const handleCopyOrgId = () => {
    if (orgId) {
      navigator.clipboard.writeText(orgId);
      setCopiedId(true);
      toast.success('Org ID copied to clipboard');
      setTimeout(() => setCopiedId(false), 2000);
    }
  };
  
  const handleSaveExternalId = async () => {
    if (!orgId) return;
    setSavingExternalId(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/rbac/organizations/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ external_id: externalId.trim() })
      });
      if (res.ok) {
        toast.success('External ID saved');
        setOrgRecord(prev => prev ? { ...prev, external_id: externalId.trim() } : prev);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save external ID');
      }
    } catch (e) {
      toast.error('Failed to save external ID');
    } finally {
      setSavingExternalId(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }
    setIsUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Logo = e.target.result;
        try {
          await uploadLogo(base64Logo);
          setCompanyLogo(base64Logo);
          toast.success('Logo uploaded successfully!');
        } catch (error) {
          toast.error('Failed to upload logo: ' + (error.response?.data?.detail || error.message));
        }
        setIsUploadingLogo(false);
      };
      reader.onerror = () => { toast.error('Failed to read file'); setIsUploadingLogo(false); };
      reader.readAsDataURL(file);
    } catch (error) { toast.error('Failed to upload logo'); setIsUploadingLogo(false); }
  };

  const handleDeleteLogo = async () => {
    setIsUploadingLogo(true);
    try { await deleteLogo(); setCompanyLogo(null); toast.success('Logo removed'); }
    catch (error) { toast.error('Failed to remove logo'); }
    finally { setIsUploadingLogo(false); }
  };

  const handleSavePreset = async () => {
    if (!newPreset.name.trim()) { toast.error('Please enter a preset name'); return; }
    setSavingPreset(true);
    try {
      const presetData = {
        name: newPreset.name.trim(), description: newPreset.description.trim(),
        icon: newPreset.icon, color: newPreset.color,
        settings: {
          preferred_elasticity_scenario: profile.preferred_elasticity_scenario,
          current_maturity: profile.current_maturity,
          dampening_factors: profile.dampening_factors,
          scale_dampening_config: profile.scale_dampening_config
        }
      };
      const response = await createCustomPreset(presetData);
      setCustomPresets(prev => [response.data, ...prev]);
      setSavePresetDialogOpen(false);
      setNewPreset({ name: '', description: '', icon: '⚙️', color: 'slate' });
      toast.success(`Preset "${presetData.name}" saved!`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save preset'); }
    finally { setSavingPreset(false); }
  };

  const handleDeletePreset = async (presetId) => {
    try { await deleteCustomPreset(presetId); setCustomPresets(prev => prev.filter(p => p.preset_id !== presetId)); setDeleteConfirmId(null); toast.success('Preset deleted'); }
    catch (error) { toast.error('Failed to delete preset'); }
  };

  const applyCustomPreset = (preset) => {
    setProfile(prev => ({
      ...prev, ...preset.settings,
      dampening_factors: { ...prev.dampening_factors, ...preset.settings.dampening_factors }
    }));
    toast.success(`Applied "${preset.name}" preset`);
  };

  const getPresetColorClass = (color) => {
    const colorMap = {
      blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
      teal: 'border-teal-200 hover:border-teal-400 hover:bg-teal-50',
      purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
      amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
      slate: 'border-slate-200 hover:border-slate-400 hover:bg-slate-50',
      orange: 'border-orange-200 hover:border-orange-400 hover:bg-orange-50',
      rose: 'border-rose-200 hover:border-rose-400 hover:bg-rose-50',
      emerald: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50',
    };
    return colorMap[color] || colorMap.slate;
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (orgId) {
        // Save to org record via RBAC API
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(`${API_URL}/api/rbac/organizations/${orgId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            name: profile.organization_name || orgName,
            settings: { ...profile, company_logo: companyLogo }
          })
        });
        if (res.ok) {
          setOrgName(profile.organization_name || orgName);
          toast.success('Organization settings saved');
          if (onSaved) onSaved(orgId);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to save settings');
        }
      } else {
        // Fallback: save to user profile
        await updateProfile({ profile });
        toast.success('Organization settings saved');
      }
    } catch (err) { toast.error(err.message || 'Failed to save settings'); }
    finally { setIsLoading(false); }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: profile.default_currency || 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="org-profile" className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="org-profile" className="gap-1.5 text-xs sm:text-sm" data-testid="org-settings-profile-tab">
            <Building2 className="h-3.5 w-3.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="org-financials" className="gap-1.5 text-xs sm:text-sm" data-testid="org-settings-financials-tab">
            <DollarSign className="h-3.5 w-3.5" /> Financials
          </TabsTrigger>
          <TabsTrigger value="org-defaults" className="gap-1.5 text-xs sm:text-sm" data-testid="org-settings-defaults-tab">
            <Settings className="h-3.5 w-3.5" /> Defaults
          </TabsTrigger>
          <TabsTrigger value="org-industry" className="gap-1.5 text-xs sm:text-sm" data-testid="org-settings-industry-tab">
            <Layers className="h-3.5 w-3.5" /> Industry
          </TabsTrigger>
          <TabsTrigger value="org-mirror" className="gap-1.5 text-xs sm:text-sm" data-testid="org-settings-mirror-tab">
            <MessageSquareText className="h-3.5 w-3.5" /> Mirror
          </TabsTrigger>
        </TabsList>

        {/* Organization Profile Sub-tab */}
        <TabsContent value="org-profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-5 w-5 text-teal-600" /> Organization Profile
              </CardTitle>
              <CardDescription>These values will be used as defaults when running analyses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Org ID & External ID */}
              {orgId && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-500" /> Organization ID
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 px-3 py-2 bg-white border rounded-md text-sm font-mono text-slate-600 select-all" data-testid="org-id-display">
                        {orgId}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCopyOrgId}
                        className="shrink-0"
                        data-testid="copy-org-id-btn"
                      >
                        {copiedId ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Internal system identifier for API integration</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-slate-500" /> External ID
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="text"
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        placeholder="e.g., HRIS-001, LMS-ORG-42, SF-ACC-123"
                        className="flex-1 font-mono text-sm"
                        data-testid="external-id-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveExternalId}
                        disabled={savingExternalId || externalId === (orgRecord?.external_id || '')}
                        className="shrink-0"
                        data-testid="save-external-id-btn"
                      >
                        {savingExternalId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Link this organization to an external source system (HRIS, LMS, data warehouse)</p>
                  </div>
                  <OrgTimezoneField orgId={orgId} value={orgRecord?.timezone} onSaved={(tz) => setOrgRecord((prev) => (prev ? { ...prev, timezone: tz } : prev))} />
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium text-slate-700">Organization Name</Label>
                <Input type="text" value={profile.organization_name} onChange={(e) => setProfile({ ...profile, organization_name: e.target.value })} className="mt-1" data-testid="org-name-input" />
              </div>

              {/* Company Logo */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Image className="h-4 w-4" /> Company Logo
                </Label>
                <p className="text-xs text-slate-500">Your logo will appear on exported Board Ready reports</p>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-20 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                    {companyLogo ? (
                      <img src={companyLogo} alt="Company logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <div className="text-center text-slate-400">
                        <Image className="h-6 w-6 mx-auto mb-1" /><span className="text-xs">No logo</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif" className="hidden" onChange={handleLogoUpload} disabled={isUploadingLogo} data-testid="logo-file-input" />
                    <Button type="button" variant="outline" size="sm" className="w-full" disabled={isUploadingLogo}
                      onClick={() => { if (logoInputRef.current) logoInputRef.current.value = ''; logoInputRef.current?.click(); }}
                      data-testid="logo-upload-btn"
                    >
                      {isUploadingLogo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {companyLogo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    {companyLogo && (
                      <Button type="button" variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={handleDeleteLogo} disabled={isUploadingLogo} data-testid="logo-delete-btn">
                        <Trash2 className="h-4 w-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400">Recommended: PNG or JPG, max 2MB, ideally 200x80px</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Industry</Label>
                  <Select value={profile.industry_type} onValueChange={(v) => setProfile({ ...profile, industry_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="industry-select"><SelectValue placeholder="Select an industry…" /></SelectTrigger>
                    <SelectContent>
                      {industryProfiles.length === 0 ? (
                        <SelectItem value="other" disabled>Loading industries…</SelectItem>
                      ) : (
                        industryProfiles.map((ind) => (
                          <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Business Type</Label>
                  <Select value={profile.business_type} onValueChange={(v) => setProfile({ ...profile, business_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="business-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enterprise">Enterprise (B2B)</SelectItem>
                      <SelectItem value="consumer">Consumer (B2C)</SelectItem>
                      <SelectItem value="hybrid">Hybrid (B2B + B2C)</SelectItem>
                      <SelectItem value="marketplace">Marketplace</SelectItem>
                      <SelectItem value="nonprofit">Non-Profit</SelectItem>
                      <SelectItem value="government">Government</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Company Size</Label>
                  <Select value={profile.company_size} onValueChange={(v) => setProfile({ ...profile, company_size: v })}>
                    <SelectTrigger className="mt-1" data-testid="company-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="startup">Startup (&lt;50)</SelectItem>
                      <SelectItem value="small">Small (50-500)</SelectItem>
                      <SelectItem value="medium">Medium (500-2,000)</SelectItem>
                      <SelectItem value="large">Large (2,000-10,000)</SelectItem>
                      <SelectItem value="enterprise">Enterprise (&gt;10,000)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Region</Label>
                  <Select value={profile.region || 'north_america'} onValueChange={(v) => setProfile({ ...profile, region: v })}>
                    <SelectTrigger className="mt-1" data-testid="region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="north_america">North America</SelectItem>
                      <SelectItem value="europe">Europe</SelectItem>
                      <SelectItem value="asia_pacific">Asia Pacific</SelectItem>
                      <SelectItem value="latin_america">Latin America</SelectItem>
                      <SelectItem value="middle_east">Middle East & Africa</SelectItem>
                      <SelectItem value="global">Global (Multiple Regions)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Total Workforce</Label>
                  <Input type="number" value={profile.total_workforce} onChange={(e) => setProfile({ ...profile, total_workforce: parseInt(e.target.value) || 0 })} className="mt-1" data-testid="workforce-input" />
                  <p className="text-xs text-slate-500 mt-1">Total number of employees</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Eligible Population</Label>
                  <Input type="number" value={profile.eligible_population} onChange={(e) => setProfile({ ...profile, eligible_population: parseInt(e.target.value) || 0 })} className="mt-1" data-testid="eligible-pop-input" />
                  <p className="text-xs text-slate-500 mt-1">Employees eligible for training programs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financials Sub-tab */}
        <TabsContent value="org-financials">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-5 w-5 text-teal-600" /> Financial Parameters
              </CardTitle>
              <CardDescription>Core financial metrics used in ROI and impact calculations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Default Currency</Label>
                  <Select value={profile.default_currency} onValueChange={(v) => setProfile({ ...profile, default_currency: v })}>
                    <SelectTrigger className="mt-1" data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="AUD">AUD (A$)</SelectItem>
                      <SelectItem value="CAD">CAD (C$)</SelectItem>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="JPY">JPY (¥)</SelectItem>
                      <SelectItem value="CHF">CHF (Fr)</SelectItem>
                      <SelectItem value="SGD">SGD (S$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Fiscal Year Start</Label>
                  <Select value={profile.fiscal_year_start || 'january'} onValueChange={(v) => setProfile({ ...profile, fiscal_year_start: v })}>
                    <SelectTrigger className="mt-1" data-testid="fiscal-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="january">January</SelectItem>
                      <SelectItem value="april">April</SelectItem>
                      <SelectItem value="july">July</SelectItem>
                      <SelectItem value="october">October</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-medium text-slate-800 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-teal-600" /> Revenue Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Annual Revenue</Label>
                    <Input type="number" value={profile.annual_revenue || 0} onChange={(e) => setProfile({ ...profile, annual_revenue: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="annual-revenue-input" />
                    <p className="text-xs text-slate-500 mt-1">{formatCurrency(profile.annual_revenue || 0)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Revenue per Employee</Label>
                    <Input type="number" value={profile.revenue_per_employee} onChange={(e) => setProfile({ ...profile, revenue_per_employee: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="revenue-per-employee-input" />
                    <p className="text-xs text-slate-500 mt-1">{formatCurrency(profile.revenue_per_employee)}/employee</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Operating Margin (%)</Label>
                  <div className="flex items-center gap-4 mt-1">
                    <Slider value={[profile.operating_margin || 15]} onValueChange={([v]) => setProfile({ ...profile, operating_margin: v })} max={50} min={0} step={1} className="flex-1" />
                    <span className="w-16 text-right font-medium text-teal-600">{profile.operating_margin || 15}%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Used to calculate net impact from revenue improvements</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Default Intervention Budget</Label>
                <Input type="number" value={profile.default_intervention_budget} onChange={(e) => setProfile({ ...profile, default_intervention_budget: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="budget-input" />
                <p className="text-xs text-slate-500 mt-1">Default budget for learning interventions: {formatCurrency(profile.default_intervention_budget)}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Defaults Sub-tab */}
        <TabsContent value="org-defaults">
          <OrgDefaultsTab
            profile={profile}
            setProfile={setProfile}
            orgId={orgId}
            customPresets={customPresets}
            loadingPresets={loadingPresets}
            setSavePresetDialogOpen={setSavePresetDialogOpen}
            applyCustomPreset={applyCustomPreset}
            deleteConfirmId={deleteConfirmId}
            setDeleteConfirmId={setDeleteConfirmId}
            handleDeletePreset={handleDeletePreset}
            getPresetColorClass={getPresetColorClass}
          />
        </TabsContent>

        {/* Mirror Display Sub-tab */}
        <TabsContent value="org-mirror">
          <MirrorDisplayPanel orgId={orgId} />
        </TabsContent>

        {/* Industry Model Sub-tab */}
        <TabsContent value="org-industry">
          <IndustryModelPanel orgId={orgId} />
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end mt-4">
        <Button onClick={handleSave} disabled={isLoading} className="bg-teal-600 hover:bg-teal-700" data-testid="save-org-settings-btn">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="mr-2 h-4 w-4" />}
          Save Organization Settings
        </Button>
      </div>

      {/* Save Preset Dialog */}
      <SavePresetDialog
        open={savePresetDialogOpen}
        onOpenChange={setSavePresetDialogOpen}
        newPreset={newPreset}
        setNewPreset={setNewPreset}
        onSave={handleSavePreset}
        saving={savingPreset}
      />
    </div>
  );
}
